import styled from '@emotion/styled'
import { Skeleton } from '@mui/material'
import { useEffect } from 'react'

import {
  Attribute,
  Currency,
  DateDisplay,
  MessageBox,
  Section,
  Space
} from '../../../../../components/core-v2'
import { useAppSelector } from '../../../store/hooks'
import { useActions } from '../../../store/use-actions'
import { actions as rawActions, FundingSource } from '../state/terms-slice'

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 10px;
  padding: 10px 0;
`
const LoadingData = () => (
  <LoadingWrapper>
    <Skeleton height="40px" />
    <Skeleton height="40px" />
    <Skeleton height="40px" />
  </LoadingWrapper>
)

const TermsDetailsSummary = () => {
  const builderState = useAppSelector(state => state.builder)
  const portfolioState = useAppSelector(state => state.portfolio)
  const actions = useActions(rawActions)
  const state = useAppSelector(state => state.terms)
  const terms = state?.terms
  const hasTopupOrWithdraw = terms.topup || terms.withdraw
  const isClosing = builderState.savedState.account.pendingAccountState.closeAccount
  const hasAnyContribution =
    terms.investment ||
    hasTopupOrWithdraw ||
    isClosing ||
    terms.fundingSource === FundingSource.Transfer ||
    terms.fundingSource === FundingSource.Switch
  const transferUnits = portfolioState.selfSelect?.transferUnits

  useEffect(() => {
    actions.reset()

    if (builderState.note && builderState.client && builderState.businessUnit) {
      actions.init({
        note: builderState.note,
        client: builderState.client,
        businessUnit: builderState.businessUnit
      })
    }
  }, [
    builderState.termsKey,
    builderState.client,
    builderState.note,
    builderState.businessUnit,
    actions
  ])

  const investmentAttributeName = hasTopupOrWithdraw ? 'Current balance' : 'Investment amount'

  return (
    <>
      {terms && hasAnyContribution && (
        <Space mx={5} my={6}>
          {terms.accountName && <Attribute name="Trade name">{terms.accountName}</Attribute>}
          {terms.investment && (
            <Attribute name={investmentAttributeName}>
              <Currency value={terms.investment} />
            </Attribute>
          )}
          {terms.topup && (
            <Attribute name="Top up amount">
              <Currency value={terms.topup} />
            </Attribute>
          )}
          {terms.withdraw && (
            <Attribute name="Withdraw amount">
              <Currency value={terms.withdraw} />
            </Attribute>
          )}
          {isClosing && <Space>Trade closure has been requested</Space>}
          {terms.fundingSource === FundingSource.Transfer && (
            <Section mt={5} type="primary">
              <Section type="secondary">
                <Attribute name="Transfer units">Units</Attribute>
              </Section>
              {transferUnits?.map(x => (
                <Attribute key={x.assetId} name={x.assetId}>
                  {x.units}
                </Attribute>
              ))}
            </Section>
          )}
          {terms.fundingSource === FundingSource.Switch && (
            <>
              {terms.switchTrade && (
                <>
                  <Attribute name="Switch out trade">{terms.switchTrade.accountName}</Attribute>
                  {terms.fullSwitch ? (
                    <>
                      <Attribute name="Current Value of switch out Trade">
                        <Currency value={terms.switchTrade.accountValue} />
                      </Attribute>
                      <Attribute name="Full switch">Yes</Attribute>
                    </>
                  ) : (
                    <Attribute name="Switch amount">
                      <Currency value={terms.switchInvestment} />
                    </Attribute>
                  )}
                </>
              )}
              {!terms.switchTrade && <LoadingData />}
            </>
          )}

          {terms.investment && terms.requestedStartDate !== undefined && (
            <Attribute name="Requested start date">
              {terms.requestedStartDate ? (
                <DateDisplay value={terms?.requestedStartDate} />
              ) : (
                'As soon as possible'
              )}
            </Attribute>
          )}
        </Space>
      )}
      {terms && !hasAnyContribution && (
        <Space mx={5} my={6}>
          <MessageBox state="disabled">No Top up / Withdraw requested from this account</MessageBox>
        </Space>
      )}
      {terms === undefined && (
        <Space mx={5} my={6}>
          <MessageBox state="warning" title="Terms details do not exist for this trade">
            Please edit and add terms details
          </MessageBox>
        </Space>
      )}
    </>
  )
}

export default TermsDetailsSummary
